import styled from "styled-components";
import {fontFamilies} from "theme/tokens";
import {CLOSE_BUTTON_INSET, CLOSE_BUTTON_SIZE} from "./gameGeometry";

/**
 * Externalized styles for {@link ./GameFrame}, the frame every transfer game draws in
 * (kitecorp/transferify-ui#2872, #2874), after the approved prototypes. Every colour reads `theme.tokens`.
 */

export const GameRoot = styled.div`
	position: relative;
	border-radius: 14px;
	background: ${({theme}) => theme.tokens.field};
	border: 1px solid ${({theme}) => theme.tokens.fieldLine};
	overflow: hidden;
	flex: none;

	&:focus-within {
		box-shadow: 0 0 0 2px ${({theme}) => theme.tokens.focusOutline};
	}
`;

/**
 * Each game has its own height (`./gameGeometry`). `$dragged` is a game played by holding and dragging
 * (File Invaders): the browser must not take that gesture for a pan and cancel the pointer mid-drag.
 */
export const GameCanvas = styled.canvas<{$height: number; $dragged: boolean}>`
	display: block;
	width: 100%;
	height: ${({$height}) => $height}px;
	touch-action: ${({$dragged}) => ($dragged ? "none" : "manipulation")};
	cursor: pointer;

	&:focus-visible {
		outline: none;
	}
`;

/** 40px square: the mobile close target, above the canvas and the overlay. */
export const CloseButton = styled.button`
	position: absolute;
	top: ${CLOSE_BUTTON_INSET}px;
	right: ${CLOSE_BUTTON_INSET}px;
	z-index: 2;
	width: ${CLOSE_BUTTON_SIZE}px;
	height: ${CLOSE_BUTTON_SIZE}px;
	display: grid;
	place-items: center;
	border: 0;
	border-radius: 10px;
	background: ${({theme}) => theme.tokens.card};
	color: ${({theme}) => theme.tokens.ink2};
	cursor: pointer;

	&:hover {
		color: ${({theme}) => theme.tokens.ink};
	}

	&:focus-visible {
		outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
		outline-offset: 2px;
	}
`;

/**
 * `--game-overlay-padding` lets a slot give its panels more of the canvas: the recipient page's finish
 * screen carries three buttons inside the shortest game (kitecorp/transferify-ui#2889).
 */
export const Overlay = styled.div`
	position: absolute;
	inset: 0;
	display: grid;
	place-items: center;
	text-align: center;
	padding: var(--game-overlay-padding, 16px);
	pointer-events: none;
`;

/** `$wide`: a finish screen with a row of buttons, which needs the width to keep them on one line. */
export const Panel = styled.div<{$wide?: boolean}>`
	pointer-events: auto;
	display: grid;
	gap: 4px;
	justify-items: center;
	max-width: ${({$wide}) => ($wide ? "min(480px, 100%)" : "min(320px, 100%)")};
	padding: ${({$wide}) => ($wide ? "10px 14px" : "12px 16px")};
	border-radius: 14px;
	border: 1px solid ${({theme}) => theme.tokens.line};
	background: color-mix(in srgb, ${({theme}) => theme.tokens.card} 92%, transparent);
	font-family: ${fontFamilies.sans};
`;

export const PanelTitle = styled.h3`
	margin: 0;
	font-size: 16px;
	font-weight: 700;
	color: ${({theme}) => theme.tokens.ink};
`;

export const PanelText = styled.p`
	margin: 0;
	font-size: 13px;
	color: ${({theme}) => theme.tokens.ink2};
	font-variant-numeric: tabular-nums;
`;

export const PanelButton = styled.button`
	margin-top: 6px;
	min-height: 44px;
	display: inline-flex;
	align-items: center;
	gap: 8px;
	padding: 8px 14px;
	border: 0;
	border-radius: 10px;
	background: ${({theme}) => theme.tokens.blue};
	color: ${({theme}) => theme.tokens.blueInk};
	font: 600 13px ${fontFamilies.sans};
	cursor: pointer;

	&:hover {
		background: ${({theme}) => theme.tokens.blued};
	}

	&:focus-visible {
		outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
		outline-offset: 2px;
	}
`;
